import { useCallback, useMemo, useState } from "react";
import { DashboardAppointmentAIReport } from "../AppointmentAIReportsTableContainer/getAppointmentAIReports";
import { FormValues } from "./types";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import useDefaultErrorWrapper from "../../../hooks/useDefaultErrorWrapper";

export type AppointmentAIReportReviewEditDialogProps = {
  isOpened: boolean;
  report: DashboardAppointmentAIReport | null;
  mode: "review" | "edit";
  handleClose: () => void;
  onCancelClick: () => void;
  onSave: (values: FormValues) => void | Promise<void>;
};

const useAppointmentAIReportReviewEditDialog = (params: {
  reports: DashboardAppointmentAIReport[];
  refetch?: () => Promise<void>;
}): {
  props: AppointmentAIReportReviewEditDialogProps | null;
  isOpened: boolean;
  onOpenClick: (id: number, mode: "review" | "edit") => void;
} => {
  const { reports, refetch } = params;
  const [reportId, setReportId] = useState<number | null>(null);
  const [mode, setMode] = useState<"review" | "edit">("edit");

  const { runAsyncFunction } = useDefaultErrorWrapper();
  const startEditingAIReport = useFirebaseAppFunction("startEditingAIReport");
  const cancelEditingAIReport = useFirebaseAppFunction("cancelEditingAIReport");
  const approveAppointmentAIReport = useFirebaseAppFunction(
    "approveAppointmentAIReport",
  );

  const onOpenClick = useCallback(
    (id: number, newMode: "review" | "edit") => {
      const report = reports.find((r) => r.id === id);
      if (!report) {
        throw new Error("Report not found");
      }

      const appointmentId = report.appointment.id;

      setReportId(id);
      setMode(newMode);

      runAsyncFunction(startEditingAIReport, {
        reportId: id,
        appointmentId,
      });
    },
    [reports, runAsyncFunction, startEditingAIReport],
  );

  const onCancelClick = useCallback(() => {
    const currentReportId = reportId;

    setReportId(null);
    if (currentReportId !== null) {
      const currentReport = reports.find((r) => r.id === currentReportId);
      if (currentReport) {
        const appointmentId = currentReport.appointment.id;
        runAsyncFunction(cancelEditingAIReport, {
          reportId: currentReportId,
          appointmentId,
          mode,
        });
      }
    }
  }, [reportId, reports, runAsyncFunction, cancelEditingAIReport, mode]);

  const handleClose = useCallback(() => {
    setReportId(null);
  }, []);

  const report = useMemo(() => {
    if (!reportId) return null;
    return reports.find(({ id }) => id === reportId) || null;
  }, [reportId, reports]);

  const isOpened = !!reportId;

  const onSave = useCallback(
    async (values: FormValues) => {
      if (!report) {
        throw new Error("Report not found");
      }

      const appointmentId = report.appointment.id;

      await runAsyncFunction(approveAppointmentAIReport, {
        reportId: report.id,
        appointmentId,
        summarySection: values.summarySection,
        findingsSection: values.findingsSection,
        nextStepsSection: values.nextStepsSection,
      });

      handleClose();

      if (refetch) {
        await refetch();
      }
    },
    [
      report,
      runAsyncFunction,
      approveAppointmentAIReport,
      refetch,
      handleClose,
    ],
  );

  return {
    isOpened,
    onOpenClick,
    props: report
      ? {
          isOpened,
          report,
          mode,
          onCancelClick,
          handleClose,
          onSave,
        }
      : null,
  };
};

export default useAppointmentAIReportReviewEditDialog;
